/**
 * Dashboard Page
 * Main dashboard displaying sustainability metrics, recent activity, and quick actions
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { PageHeader, PageTitle } from '../components/layout/PageHeader';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Icon } from '../components/ui/Icon';
import { MetricCard } from '../components/dashboard/MetricCard';
import { ActivityPanel } from '../components/dashboard/ActivityPanel';
import { QuickActionsPanel } from '../components/dashboard/QuickActionsPanel';
import { RemindersPanel } from '../components/dashboard/RemindersPanel';
import { UnitReviewNotice } from '../components/dashboard/UnitReviewNotice';
import { useDashboard } from '../hooks/useApi';
import { useIndicators } from '../hooks/useIndicators';
import { DashboardData } from '../types/api';
import { formatNumberEs, formatDate } from '../utils/formatters';

const monthYearPattern = /^(?<month>[\p{L}]+)\s+(?<year>\d{4})$/u;
const monthDeYearPattern = /^(?<month>[\p{L}]+)\s+de\s+(?<year>\d{4})$/u;
const isoMonthPattern = /^(?<year>\d{4})-(?<month>\d{1,2})$/;
const monthIndexesByName: Readonly<Record<string, number>> = {
  enero: 0,
  febrero: 1,
  marzo: 2,
  abril: 3,
  mayo: 4,
  junio: 5,
  julio: 6,
  agosto: 7,
  septiembre: 8,
  octubre: 9,
  noviembre: 10,
  diciembre: 11,
  january: 0,
  february: 1,
  march: 2,
  april: 3,
  may: 4,
  june: 5,
  july: 6,
  august: 7,
  september: 8,
  october: 9,
  november: 10,
  december: 11,
};

const capitalizeFirstLetter = (value: string): string => {
  if (!value) {
    return value;
  }

  const firstCharacter = value.charAt(0).toLocaleUpperCase('es-ES');
  return `${firstCharacter}${value.slice(1)}`;
};

const parseMonthToken = (value: string): string =>
  value.toLocaleLowerCase('es-ES').normalize('NFD').replace(/\p{M}/gu, '');

const formatMonthYearLabel = (
  yearToken: string,
  monthIndex: number,
): string | undefined => {
  const year = Number(yearToken);
  if (
    !Number.isInteger(year) ||
    year < 0 ||
    monthIndex < 0 ||
    monthIndex > 11
  ) {
    return undefined;
  }

  const parsedDate = new Date(year, monthIndex, 1);
  if (Number.isNaN(parsedDate.valueOf())) {
    return undefined;
  }

  const formatted = parsedDate.toLocaleDateString('es-ES', {
    month: 'long',
    year: 'numeric',
  });
  return capitalizeFirstLetter(formatted);
};

const formatMonthLabel = (label?: string): string | undefined => {
  if (!label) {
    return undefined;
  }

  const trimmed = label.trim();
  if (!trimmed) {
    return undefined;
  }

  const isoMatch = trimmed.match(isoMonthPattern);
  if (isoMatch?.groups) {
    const isoYear = isoMatch.groups.year;
    const isoMonth = isoMatch.groups.month;
    if (typeof isoYear === 'string' && typeof isoMonth === 'string') {
      const isoLabel = formatMonthYearLabel(isoYear, Number(isoMonth) - 1);
      if (isoLabel) {
        return isoLabel;
      }
    }
  }

  const monthMatch =
    trimmed.match(monthDeYearPattern) ?? trimmed.match(monthYearPattern);
  if (monthMatch?.groups) {
    const rawMonth = monthMatch.groups.month;
    const rawYear = monthMatch.groups.year;
    if (typeof rawMonth === 'string' && typeof rawYear === 'string') {
      const monthIndex = monthIndexesByName[parseMonthToken(rawMonth)];
      if (monthIndex !== undefined) {
        const parsedLabel = formatMonthYearLabel(rawYear, monthIndex);
        if (parsedLabel) {
          return parsedLabel;
        }
      }
    }
  }

  return capitalizeFirstLetter(trimmed);
};

const formatBaselineMonthLabel = (month?: string): string | undefined => {
  if (!month) {
    return undefined;
  }

  const [yearPart, monthPart] = month.split('-');
  const year = Number(yearPart);
  const monthIndex = Number(monthPart);

  if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) {
    return capitalizeFirstLetter(month);
  }

  const parsedDate = new Date(year, monthIndex - 1, 1);
  if (Number.isNaN(parsedDate.valueOf())) {
    return capitalizeFirstLetter(month);
  }

  const formatted = parsedDate.toLocaleDateString('es-ES', {
    month: 'long',
    year: 'numeric',
  });

  return capitalizeFirstLetter(formatted);
};

const DashboardContainer = styled.div`
  width: 100%;
  min-width: 0;
  max-width: none;
  margin: 0;
  padding: 0;
`;

const DashboardHero = styled.div`
  padding: 0 0 1.5rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid var(--border);
`;

const HeroContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;

  /* Mobile devices - stack vertically */
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.25rem;
  }
`;

const HeroText = styled.div`
  flex: 1;
`;

const HeroTitle = styled(PageTitle)``;

const HeroSubtitle = styled.p`
  margin: 0.5rem 0 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const HeroMetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ScoreSection = styled.div`
  position: relative;
  @media (max-width: 767px) {
    width: 100%;
  }
  padding: 1rem 1.25rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  text-align: left;
`;

const ScoreValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
`;

const ScoreLabel = styled.div`
  font-size: 0.75rem;
  opacity: 0.9;
  margin-top: 0.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const ScoreTooltipWrapper = styled.div`
  display: inline-flex;
  align-items: center;
`;

const ScoreTooltipTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  @media (max-width: 767px) {
    width: 44px;
    height: 44px;
  }
  border-radius: 999px;
  border: 1px solid var(--border-strong);
  background: transparent;
  color: inherit;
  cursor: help;
  padding: 0;
  transition:
    background 0.2s ease,
    border-color 0.2s ease;

  &:hover,
  &:focus-visible {
    background: var(--interactive-hover);
    border-color: var(--border-strong);
  }

  &:focus-visible {
    outline: 2px solid var(--interactive-focus);
    outline-offset: 2px;
  }
`;

const ScoreTooltipContent = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: rgba(15, 23, 42, 0.95);
  color: #fff;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  font-size: 0.75rem;
  line-height: 1.4;
  width: max-content;
  max-width: min(20rem, calc(100vw - 3rem));
  z-index: 20;
  &[hidden] {
    display: none;
  }
`;

const ScoreTooltipList = styled.ul`
  margin: 0;
  padding-left: 1rem;
  display: grid;
  gap: 0.35rem;
`;

const ScoreTooltipItem = styled.li`
  list-style: disc;
`;

const MetricsContextBanner = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 1rem 0;
  font-size: 0.875rem;
`;

const MetricsContextHighlight = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  color: var(--text-secondary);
`;

const MetricsContextText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--text-muted);
  font-size: 0.8125rem;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin: 1rem 0 2rem 0;

  /* Tablet devices - 2 columns */
  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
    margin: 1.25rem 0 1.75rem 0;
  }

  /* Mobile devices - 1 column */
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin: 1rem 0 1.25rem 0;
  }

  /* Small mobile devices - optimized spacing */
  @media (max-width: 479px) {
    gap: 0.75rem;
    margin: 0.75rem 0 1rem 0;
  }

  /* Extra small phones (≤360px) */
  @media (max-width: 360px) {
    gap: 0.5rem;
    margin: 0.5rem 0 0.75rem 0;
  }
`;

const PanelsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
  gap: 1.25rem;
  > :last-child {
    grid-column: 1 / -1;
  }
  @media (max-width: 1023px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const ErrorMessage = styled.div`
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
  color: var(--danger-600);
  font-size: 0.875rem;
`;

const MissingDataWarning = styled.div`
  font-size: 0.75rem;
  color: var(--warning-600);
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.25rem;
  font-weight: 500;
`;

const RetryButton = styled.button`
  background: var(--danger-600);
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  cursor: pointer;
  margin-top: 0.5rem;

  &:hover {
    opacity: 0.9;
  }
`;

// Default data for when API is unavailable
const defaultDashboardData: DashboardData = {
  period: {
    label: 'Este mes',
    updated_at: new Date().toISOString(),
    start_date: new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      1,
    ).toISOString(),
    end_date: new Date().toISOString(),
  },
  metrics: {
    energia_kwh_per_dish: 0,
    agua_m3_per_dish: 0,
    gas_m3_per_dish: 0,
    waste_diversion_pct: 0,
    co2e_per_dish: 0,
    supplier_certification_pct: 0,
    has_covers_volume_data: false,
    puntuacion_general: 0,
    progress_score: null,
    progress_score_baseline: null,
  },
  deltas: {
    energia_kwh_per_dish: null,
    agua_m3_per_dish: null,
    gas_m3_per_dish: null,
    waste_diversion_pct: null,
    co2e_per_dish: null,
    supplier_certification_pct: null,
  },
  raw_totals: {
    energia_kwh: 0,
    agua_m3: 0,
    gas_m3: 0,
  },
  current_dishes: 0,
  recent_activity: [],
};

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [scoreHelpOpen, setScoreHelpOpen] = React.useState(false);

  const {
    data: dashboardData,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useDashboard();

  // Use API data or fall back to default data
  const data = dashboardData || defaultDashboardData;

  // Format indicators using the new hook
  const { indicators } = useIndicators(dashboardData);

  const lastMonthDisplayed = data.period?.last_month_displayed;
  const previousMonthDisplayed =
    data.data_available?.previous_month?.month_name;

  const formattedLastMonthDisplayed = React.useMemo(
    () => formatMonthLabel(lastMonthDisplayed),
    [lastMonthDisplayed],
  );

  const formattedPreviousMonthDisplayed = React.useMemo(
    () => formatMonthLabel(previousMonthDisplayed),
    [previousMonthDisplayed],
  );

  const progressScore = data.metrics?.progress_score ?? null;
  const progressScoreBaseline = data.metrics?.progress_score_baseline ?? null;

  const formattedBaselineMonths = React.useMemo(() => {
    if (!progressScoreBaseline?.months?.length) {
      return [] as string[];
    }

    return progressScoreBaseline.months
      .map((month) => formatBaselineMonthLabel(month))
      .filter((value): value is string => Boolean(value));
  }, [progressScoreBaseline]);

  const progressTooltipItems = React.useMemo(() => {
    const items: Array<string | null> = [
      'Requiere 3 meses consecutivos con cubiertos y datos de CO₂ (alcances 1 y 2), residuos y agua.',
      formattedBaselineMonths.length
        ? `Línea base: ${formattedBaselineMonths.join(', ')}`
        : null,
      'Interpretación: > 50 = mejor que la línea base; = 50 = igual; < 50 = peor.',
    ];

    return items.filter((item): item is string => Boolean(item));
  }, [formattedBaselineMonths]);

  const progressTooltipId = React.useId();

  const displayedProgressScore =
    progressScore != null ? `${formatNumberEs(progressScore, 0)}%` : '—';

  const handleRetry = () => {
    refetch();
  };

  if (isLoading && !dashboardData) {
    return (
      <DashboardContainer>
        <Card>
          <LoadingSpinner message="Cargando resumen…" />
        </Card>
      </DashboardContainer>
    );
  }

  if (!dashboardData && !isLoading) {
    return (
      <>
        <PageHeader title="Resumen" />
        <Card role="alert">
          <p>No se pudo cargar el resumen del restaurante.</p>
          <Button
            onClick={handleRetry}
            loading={isRefetching}
            style={{ marginTop: '1rem' }}
          >
            Reintentar
          </Button>
        </Card>
      </>
    );
  }

  return (
    <DashboardContainer>
      {/* Dashboard Hero Section */}
      <DashboardHero>
        <HeroContent>
          <HeroText>
            <HeroTitle>Resumen</HeroTitle>
            <HeroSubtitle>
              Sostenibilidad del restaurante ·{' '}
              {data.period?.last_month_displayed ||
                data.period?.label ||
                'este período'}
            </HeroSubtitle>
            <HeroMeta>
              <HeroMetaItem>
                <Icon name="calendar" size="xs" aria-hidden />
                Última actualización:{' '}
                {formatDate(data.period?.updated_at || '')}
              </HeroMetaItem>
              {data.period?.start_date && data.period?.end_date && (
                <HeroMetaItem>
                  <Icon name="chart" size="xs" aria-hidden />
                  Período: {formatDate(data.period.start_date)} -{' '}
                  {formatDate(data.period.end_date)}
                </HeroMetaItem>
              )}
            </HeroMeta>
            <Button
              variant="secondary"
              onClick={() => navigate('/data-entry')}
              style={{ marginTop: '1rem' }}
            >
              Añadir datos
            </Button>
          </HeroText>

          <ScoreSection>
            <ScoreValue
              aria-live="polite"
              aria-label={
                progressScore != null
                  ? `Puntuación de progreso ${formatNumberEs(progressScore, 0)} por ciento`
                  : 'Puntuación de progreso no disponible'
              }
            >
              {displayedProgressScore}
            </ScoreValue>
            <ScoreLabel>
              <span>Puntuación de Progreso</span>
              <ScoreTooltipWrapper>
                <ScoreTooltipTrigger
                  type="button"
                  aria-label="Más información sobre la Puntuación de Progreso"
                  aria-describedby={progressTooltipId}
                  aria-expanded={scoreHelpOpen}
                  onClick={() => setScoreHelpOpen(!scoreHelpOpen)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setScoreHelpOpen(false);
                  }}
                >
                  <Icon name="info" size="xs" aria-hidden />
                </ScoreTooltipTrigger>
                <ScoreTooltipContent
                  id={progressTooltipId}
                  role="tooltip"
                  hidden={!scoreHelpOpen}
                >
                  <ScoreTooltipList>
                    {progressTooltipItems.map((item) => (
                      <ScoreTooltipItem key={item}>{item}</ScoreTooltipItem>
                    ))}
                  </ScoreTooltipList>
                </ScoreTooltipContent>
              </ScoreTooltipWrapper>
            </ScoreLabel>
          </ScoreSection>
        </HeroContent>
      </DashboardHero>

      {/* Error Message */}
      {error && (
        <ErrorMessage>
          <div>
            <Icon
              name="warning"
              size="sm"
              color="var(--danger-600)"
              aria-hidden
            />
            Error al cargar algunos datos del resumen
          </div>
          <RetryButton onClick={handleRetry} disabled={isRefetching}>
            {isRefetching ? 'Reintentando...' : 'Reintentar'}
          </RetryButton>
        </ErrorMessage>
      )}

      {/* Metrics Cards */}
      {(formattedLastMonthDisplayed || formattedPreviousMonthDisplayed) && (
        <MetricsContextBanner>
          {formattedLastMonthDisplayed && (
            <MetricsContextHighlight>
              <Icon name="calendar" size="sm" aria-hidden />
              Datos de {formattedLastMonthDisplayed}
            </MetricsContextHighlight>
          )}

          {formattedPreviousMonthDisplayed && (
            <MetricsContextText>
              Comparado con {formattedPreviousMonthDisplayed}
            </MetricsContextText>
          )}
        </MetricsContextBanner>
      )}

      <UnitReviewNotice count={data.unit_review_count ?? 0} />

      <MetricsGrid>
        {indicators.map((indicator) => (
          <div key={indicator.id}>
            <MetricCard
              iconName={indicator.iconName as any}
              title={indicator.title}
              value={indicator.value}
              unit={indicator.unit}
              improvementDirection={
                ['waste_diversion', 'suppliers'].includes(indicator.id)
                  ? 'increase'
                  : 'decrease'
              }
              delta={indicator.delta}
              deltaValue={indicator.deltaValue ?? null}
              tooltipMessage={indicator.tooltipMessage}
            />
            {indicator.showMissingDataWarning && (
              <MissingDataWarning>
                <Icon name="warning" size="xs" />
                {indicator.missingDataMessage}
              </MissingDataWarning>
            )}
          </div>
        ))}
      </MetricsGrid>

      {/* Dashboard Panels */}
      <PanelsGrid>
        <ActivityPanel activities={data.recent_activity || []} />
        <QuickActionsPanel onNavigate={navigate} />
        <RemindersPanel />
      </PanelsGrid>
    </DashboardContainer>
  );
};

export default DashboardPage;
